import {
	Box,
	Card,
	createStyles,
	Divider,
	Group,
	TextInput,
} from '@mantine/core';
import type { CatalogRule } from '@repo/api-codegen';
import { type FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter';
import { FilterSkeleton } from '@repo/common/components/Filter/FilterSkeleton';
import { TopLevelOperatorToggle } from '@repo/common/components/Filter/TopLevelOperatorToggle';
import type { FilterOption } from '@repo/common/components/Filter/types';
import { useMobxReaction } from '@repo/common/utils';
import { Icon, IconButton, ResourceIcon, Text } from '@repo/foundations';
import { buttonHoverTransition } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useCatalogList } from '../../api/hooks/resourceCatalog';
import CatalogPreviewModal from '../../components/CatalogPreviewModal/CatalogPreviewModal';
import { openModal } from '../../components/ModalManager';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import { getCountDisplay } from '../../pages/PoliciesPage/utils';
import { FilterRule } from './components/FilterRule';
import { FilterBlockStore } from './store';

const useStyles = createStyles(
	(
		theme,
		{
			variant,
			removeBottomRadius,
		}: { variant: 'primary' | 'tertiary'; removeBottomRadius?: boolean }
	) => ({
		titleGroup: {
			backgroundColor:
				variant === 'tertiary'
					? theme.other.getColor('surface/secondary/default')
					: theme.other.getColor('surface/primary/default'),
			'&:not(:hover) .right-component-hover': {
				opacity: 0,
			},
			'&:hover .right-component-hover': {
				opacity: 1,
				...buttonHoverTransition('opacity'),
			},
			borderRadius: theme.radius.md,
			...(removeBottomRadius && {
				borderBottomRightRadius: 0,
				borderBottomLeftRadius: 0,
			}),
		},
		titleGroupContent: {
			gap: theme.spacing.sm,
		},
		resourceCountFooter: {
			padding: theme.spacing.xs,
			borderRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('surface/info/default'),
		},
		limitSearchFooter: {
			padding: theme.spacing.xs,
			borderRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('surface/critical/default'),
		},
		errorFooter: {
			padding: theme.spacing.xs,
			borderRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('surface/critical/default'),
		},
		viewResources: {
			color: theme.other.getColor('text/info/default'),
			cursor: 'pointer',
			textDecoration: 'underline',
		},
		card: {
			overflow: 'visible',
		},
		ruleNameInput: {
			background: 'transparent',
			color: theme.other.getColor('text/primary/default'),
			border: 0,
			fontSize: theme.fontSizes.sm,
			lineHeight: theme.other.typography.lineHeight.text.sm,
			height: 'unset',
			padding: 0,
			maxWidth: `${theme.other.width.sm}px`,
			'&:focus': {
				boxShadow: 'none',
			},
		},
		ruleBox: {
			borderRadius: theme.radius.md,
			padding: theme.spacing.md,
			width: '100%',
		},
		rule: {
			height: theme.other.space[7],
		},
	})
);

interface FilterBlockControllerInternalProps {
	store: FilterBlockStore;
	displayPreview?: boolean;
	displayTitle?: boolean;
	onFilterResourcesDelete?: () => void;
	variant?: 'primary' | 'tertiary';
	errorOnEmpty?: boolean;
	ruleName?: string;
	customSuffix?: string;
	readOnly?: boolean;
	limitSearch?: boolean;
	tooltip?: string;
	backgroundColor?: ColorNames;
	className?: string;
	removeBottomRadius?: boolean;
	footer?: ReactNode;
	ruleDescription?: string;
	customRuleText?: string;
	onRuleNameChange?: (value: string) => void;
	collapsible?: boolean;
	customHeader?: ReactNode;
	hideFilterRemoveButton?: boolean;
	disableTopLevelOperatorToggle?: boolean;
	initialRule: CatalogRule;
}

const FilterBlockControllerInternal = observer(
	({
		store,
		displayPreview = true,
		errorOnEmpty = false,
		displayTitle = false,
		variant = 'primary',
		onFilterResourcesDelete,
		ruleName,
		customRuleText,
		readOnly = false,
		limitSearch = false,
		tooltip,
		backgroundColor = 'fill/secondary/default',
		className,
		removeBottomRadius = false,
		footer,
		onRuleNameChange,
		collapsible = false,
		customHeader,
		hideFilterRemoveButton = false,
		customSuffix,
		disableTopLevelOperatorToggle = false,
		initialRule,
	}: FilterBlockControllerInternalProps) => {
		const { classes } = useStyles({ variant, removeBottomRadius });

		// Modal
		const { data: resources, isLoading: resourcesLoading } = useCatalogList({
			page: 1,
			filters: {
				filter: store.catalogFilter,
				grab_all_entity_types: true,
			},
		});

		const [localRuleName, setLocalRuleName] = useState(ruleName || '');
		const [isCollapsed, setIsCollapsed] = useState(
			store.filterRules.length > 0 ? collapsible : false
		);

		const handleToggleCollapsed = useCallback(() => {
			if (collapsible) {
				setIsCollapsed(!isCollapsed);
			}
		}, [collapsible, isCollapsed]);

		const debouncedOnRuleNameChange = useDebouncedCallbackWithFlush(
			(newRuleName: string) => {
				onRuleNameChange?.(newRuleName);
			},
			300
		);

		const handleRuleNameChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const newRuleName = event.currentTarget.value;
				setLocalRuleName(newRuleName);
				debouncedOnRuleNameChange(newRuleName);
			},
			[debouncedOnRuleNameChange]
		);

		const handleClick = useCallback(() => {
			openModal({
				modalId: 'resource-selection',
				variant: 'default',
				title: 'Resources',
				children: (
					<CatalogPreviewModal
						filter={
							store.catalogFilter ?? {
								operator: 'and',
								operands: [],
							}
						}
					/>
				),
			});
		}, [store.catalogFilter]);

		const exceedLimit = useMemo(() => {
			if (!limitSearch) {
				return false;
			}

			return !!resources?.count && resources.count > 100000;
		}, [limitSearch, resources?.count]);

		const exceedsChildFilterLimit = useMemo(() => {
			if (!store.includeChildFilters || !resources?.count) {
				return false;
			}

			return resources.count > 1000;
		}, [store.includeChildFilters, resources?.count]);

		const title = useMemo(() => {
			if (displayTitle) {
				return 'Filter resources';
			}

			const prefix =
				store.filterRules.length === 0 ? 'No' : store.filterRules.length;
			const rule = ruleName && `${ruleName} `;
			let suffix = store.filterRules.length === 1 ? 'rule' : 'rules';

			if (customSuffix) {
				suffix =
					store.filterRules.length === 1 ? customSuffix : `${customSuffix}s`;
			}

			return `${prefix} ${rule} ${suffix}`;
		}, [displayTitle, ruleName, store.filterRules.length, customSuffix]);

		const error = errorOnEmpty && store.filterRules.length === 0;

		const renderRuleText = () => {
			let suffix = store.filterRules.length === 1 ? 'rule' : 'rules';

			if (customSuffix) {
				suffix =
					store.filterRules.length === 1 ? customSuffix : `${customSuffix}s`;
			}

			const ruleContent =
				!displayTitle && onRuleNameChange !== undefined ? (
					<TextInput
						value={localRuleName}
						onChange={handleRuleNameChange}
						variant="unstyled"
						size="sm"
						readOnly={readOnly}
						classNames={{ input: classes.ruleNameInput }}
						placeholder={`${store.filterRules.length} ${ruleName ? `${ruleName} ` : ''}${suffix}`}
					/>
				) : (
					<Text size="sm">
						{customRuleText
							? customRuleText
							: `${store.filterRules.length === 0 ? 'No' : store.filterRules.length}${' '}
						${ruleName ? `${ruleName} ` : ''}
						${suffix}`}
					</Text>
				);

			if (collapsible && store.filterRules.length > 0) {
				return (
					<Group
						spacing="xs"
						onClick={handleToggleCollapsed}
						sx={{ cursor: 'pointer' }}
					>
						{ruleContent}
						<Icon
							name={isCollapsed ? 'chevronRight' : 'chevronDown'}
							color="icon/secondary/default"
						/>
					</Group>
				);
			}

			return ruleContent;
		};

		return (
			<Card
				withBorder
				radius="md"
				className={`${classes.card}${className ? ` ${className}` : ''}`}
			>
				{customHeader ? (
					<Card.Section>{customHeader}</Card.Section>
				) : (
					<Card.Section
						withBorder={
							!collapsible || !isCollapsed
								? store.filterRules.length > 0 ||
									displayPreview ||
									initialRule.operands.length > 0
								: false
						}
					>
						<Group position="apart" p="md" className={classes.titleGroup}>
							<Group className={classes.titleGroupContent}>
								{displayTitle && (
									<ResourceIcon
										iconName="filter"
										backgroundColor={backgroundColor}
									/>
								)}
								{displayTitle && (
									<Text size="sm" weight="semibold">
										{title}
									</Text>
								)}
								{!displayTitle && renderRuleText()}
							</Group>
							{(!collapsible || !isCollapsed) && (
								<Group spacing="3xs">
									{store.filterRules.length > 1 && (
										<TopLevelOperatorToggle
											value={store.topLevelOperator}
											onChange={store.setTopLevelOperator}
											toggleLabel="rules"
											readOnly={readOnly || disableTopLevelOperatorToggle}
										/>
									)}
									{!readOnly && (
										<IconButton
											iconName="plus"
											onClick={store.addNewRule}
											variant="tertiary"
											tooltip="Add rule"
										/>
									)}
									{displayTitle && (
										<IconButton
											iconName="x"
											onClick={onFilterResourcesDelete}
											variant="tertiary"
											tooltip={tooltip ?? 'Remove filter'}
											className="right-component-hover"
											includeOffset
										/>
									)}
								</Group>
							)}
						</Group>
					</Card.Section>
				)}
				{(!collapsible || !isCollapsed) && (
					<Card.Section>
						{store.filtersLoading ? (
							<>
								{initialRule.operands.map((operand, index) => (
									<Box key={`filter-rule-skeleton-${index}`}>
										{index > 0 && <Divider mx="sm" />}
										<Box className={classes.ruleBox}>
											<Group spacing="2xs" className={classes.rule}>
												{operand.operands.map((_, inner_index) => (
													<FilterSkeleton
														key={`filter-skeleton-${index}-${inner_index}`}
													/>
												))}
											</Group>
										</Box>
									</Box>
								))}
							</>
						) : (
							<>
								{store.filterRules.map((_, index) => (
									// eslint-disable-next-line react/no-array-index-key
									<Box key={`filter-rule-${index}`}>
										{index > 0 && <Divider mx="sm" />}
										<FilterRule
											hideFilterRemoveButton={hideFilterRemoveButton}
											filterIndex={index}
											store={store}
											readOnly={readOnly}
										/>
									</Box>
								))}
							</>
						)}
					</Card.Section>
				)}
				{(!collapsible || !isCollapsed) &&
					displayPreview &&
					!error &&
					!exceedLimit &&
					!resourcesLoading && (
						<Card.Section withBorder={store.filterRules.length > 0} p="md">
							<Group spacing="xs" className={classes.resourceCountFooter}>
								<Icon name="infoCircleFilled" color="icon/info/default" />
								<Text size="sm" color="text/info/default">
									{getCountDisplay(resources?.count)} resources matching{' '}
									{customSuffix ? customSuffix : 'rule'}
									{store.filterRules.length > 1 && 's'}.{' '}
									<span className={classes.viewResources} onClick={handleClick}>
										View resources
									</span>
								</Text>
							</Group>
						</Card.Section>
					)}
				{(!collapsible || !isCollapsed) &&
					displayPreview &&
					!error &&
					exceedLimit && (
						<Card.Section withBorder={store.filterRules.length > 0} p="md">
							<Group spacing="xs" className={classes.limitSearchFooter}>
								<Icon
									name="alertCircleFilled"
									color="surface/critical/default"
									fillColor="icon/critical/default"
								/>
								<Text size="sm" color="text/critical/default">
									Search results are limited to 100,000 resources. Please refine
									your filters.
								</Text>
							</Group>
						</Card.Section>
					)}
				{(!collapsible || !isCollapsed) && error && (
					<Card.Section withBorder p="md">
						<Group spacing="xs" className={classes.errorFooter}>
							<Icon
								name="alertCircleFilled"
								color="surface/critical/default"
								fillColor="icon/critical/default"
							/>
							<Text size="sm" color="text/critical/default">
								At least one {ruleName && `${ruleName} `}rule is required
							</Text>
						</Group>
					</Card.Section>
				)}
				{(!collapsible || !isCollapsed) && exceedsChildFilterLimit && (
					<Card.Section withBorder={store.filterRules.length > 0} p="md">
						<Group spacing="xs" className={classes.resourceCountFooter}>
							<Icon name="infoCircleFilled" color="icon/info/default" />
							<Text size="sm" color="text/info/default">
								Search results must result in sub 1000 resources prior to
								applying children filter(s).
							</Text>
						</Group>
					</Card.Section>
				)}
				{(!collapsible || !isCollapsed) && footer && (
					<Card.Section withBorder p="md">
						{footer}
					</Card.Section>
				)}
			</Card>
		);
	}
);

interface FilterBlockControllerProps
	extends Pick<
		FilterBlockControllerInternalProps,
		| 'displayPreview'
		| 'errorOnEmpty'
		| 'displayTitle'
		| 'onFilterResourcesDelete'
		| 'variant'
		| 'readOnly'
		| 'limitSearch'
		| 'tooltip'
		| 'backgroundColor'
		| 'className'
		| 'removeBottomRadius'
		| 'footer'
		| 'ruleDescription'
		| 'ruleName'
		| 'onRuleNameChange'
		| 'customRuleText'
		| 'collapsible'
		| 'customHeader'
		| 'hideFilterRemoveButton'
		| 'customSuffix'
		| 'disableTopLevelOperatorToggle'
		| 'disableTopLevelOperatorToggle'
	> {
	initialRule: CatalogRule;
	onRuleChange?: (rule: CatalogRule) => void;
	filterOptions: (FilterOption | typeof FILTER_OPTIONS_DIVIDER)[];
	includeEmptyRule?: boolean;
}

export const FilterBlockController = observer(
	({
		initialRule,
		onRuleChange,
		filterOptions,
		ruleName,
		onRuleNameChange,
		customHeader,
		includeEmptyRule = false,
		...rest
	}: FilterBlockControllerProps) => {
		const [store] = useState(
			() => new FilterBlockStore(filterOptions, initialRule, includeEmptyRule)
		);

		useMobxReaction(
			useCallback(() => store.catalogFilter, [store]),
			useCallback(
				(filter) => {
					onRuleChange?.(
						toJS(
							filter ?? {
								operator: 'and',
								operands: [],
							}
						)
					);
				},
				[onRuleChange]
			)
		);

		return (
			<FilterBlockControllerInternal
				{...rest}
				store={store}
				ruleName={ruleName}
				onRuleNameChange={onRuleNameChange}
				customHeader={customHeader}
				initialRule={initialRule}
			/>
		);
	}
);
